import UtsavCheckout from "@/pages/UtsavCheckout";
import PaytmLinks from "@/pages/PaytmLinks";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import DashboardLayout from "@/components/DashboardLayout";
import VersionChecker from "@/components/VersionChecker";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Pay from "@/pages/Pay";
import Dashboard from "@/pages/Dashboard";
import Transactions from "@/pages/Transactions";
import VPAs from "@/pages/VPAs";
import UsersPage from "@/pages/Users";
import Earnings from "@/pages/Earnings";
import SettingsPage from "@/pages/Settings";
import Developer from "@/pages/Developer";
import SetuLinks from "@/pages/SetuLinks";
import Payouts from "@/pages/Payouts";
import Statements from "@/pages/Statements";
import Preferences from "@/pages/Preferences";
import BankAccounts from "@/pages/BankAccounts";
import BankAccountRequests from "@/pages/BankAccountRequests";
import Wallets from "@/pages/Wallets";
import WalletRequests from "@/pages/WalletRequests";
import Disputes from "@/pages/Disputes";
import DepositReport from "@/pages/DepositReport";
import WithdrawReport from "@/pages/WithdrawReport";
import ProviderBalances from "@/pages/ProviderBalances";
import ProviderPayments from "@/pages/ProviderPayments";
import PaymentLinks from "@/pages/PaymentLinks";
import PaymentLink from "@/pages/PaymentLink";
import { Loader2 } from "lucide-react";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null)
    return (
      <div className="min-h-screen grid place-items-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function RoleOnly({ roles, children }) {
  const { user } = useAuth();
  if (!roles.includes(user.role)) return <Navigate to="/app" replace />;
  return children;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <VersionChecker />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/checkout/utsav/:token" element={<UtsavCheckout />} />
            <Route path="/pay/:orderId" element={<Pay />} />
            <Route path="/pl/:linkId" element={<PaymentLink />} />
            <Route
              path="/app"
              element={
                <Protected>
                  <DashboardLayout />
                </Protected>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="transactions" element={<Transactions />} />
              <Route path="payment-links" element={<PaymentLinks />} />
              <Route path="earnings" element={<Earnings />} />
              <Route path="statements" element={<Statements />} />
              <Route path="payouts" element={<Payouts />} />
              <Route path="bank-accounts" element={<BankAccounts />} />
              <Route path="bank-account-requests" element={<RoleOnly roles={["merchant", "super_admin", "admin"]}><BankAccountRequests /></RoleOnly>} />
              <Route path="wallets" element={<Wallets />} />
              <Route path="wallet-requests" element={<WalletRequests />} />
              <Route path="disputes" element={<Disputes />} />
              <Route path="deposit-report" element={<DepositReport />} />
              <Route path="withdraw-report" element={<RoleOnly roles={["super_admin", "admin", "distributor"]}><WithdrawReport /></RoleOnly>} />
              <Route path="provider-balances" element={<RoleOnly roles={["super_admin", "admin", "distributor"]}><ProviderBalances /></RoleOnly>} />
              <Route path="provider-payments" element={<RoleOnly roles={["super_admin", "admin"]}><ProviderPayments /></RoleOnly>} />
              <Route path="developer" element={<RoleOnly roles={["merchant"]}><Developer /></RoleOnly>} />
              <Route path="preferences" element={<RoleOnly roles={["merchant", "super_admin"]}><Preferences /></RoleOnly>} />
              <Route path="vpas" element={<RoleOnly roles={["merchant"]}><VPAs /></RoleOnly>} />
              <Route path="users" element={<RoleOnly roles={["super_admin", "distributor"]}><UsersPage /></RoleOnly>} />
              <Route path="settings" element={<RoleOnly roles={["super_admin"]}><SettingsPage /></RoleOnly>} />
              <Route path="paytm" element={<RoleOnly roles={["super_admin"]}><PaytmLinks /></RoleOnly>} />
              <Route path="setu" element={<RoleOnly roles={["super_admin"]}><SetuLinks /></RoleOnly>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="bottom-right" />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
